import React from 'react';
import { Card, CardContent } from './ui/card';

/**
 * DailyInsight Component
 * Displays the daily insight/report in a consistent format across the platform
 * 
 * Props:
 * - childName: string - Name of the child
 * - date: string - Date of the session (ISO or display format)
 * - focus: string - Today's focus topic
 * - snapshot: string - Brief summary of the session
 * - brightSpots: array - List of positive observations
 * - whatWeNoticed: array - List of observations/areas to work on
 * - homeConnection: string - Suggestion for parents
 * - tomorrowSeed: string - Topic for next session
 * - transcript: string - Raw transcript (optional)
 * - report: string - Raw report text (optional, will be parsed)
 * - compact: boolean - Show compact version (default: false)
 */

const DailyInsight = ({ 
  childName = 'Child',
  date,
  focus = 'Daily conversation & emotional check-in',
  snapshot = '',
  brightSpots = [],
  whatWeNoticed = [],
  homeConnection = '',
  tomorrowSeed = '',
  transcript = '',
  report = '',
  compact = false
}) => {
  
  // Parse report text if provided (extract sections)
  const parseReport = (reportText) => {
    if (!reportText) return {};
    
    const parsed = {};
    
    // Try to extract sections from report
    const snapshotMatch = reportText.match(/Snapshot[:\s]*([^🔎✨💬🌻]+)/i);
    if (snapshotMatch) parsed.snapshot = snapshotMatch[1].trim();
    
    const brightMatch = reportText.match(/Bright Spots[✨:\s]*([^🔎💬🌻]+)/i);
    if (brightMatch) {
      parsed.brightSpots = brightMatch[1]
        .split(/[\n•\-]/)
        .filter(s => s.trim())
        .map(s => s.trim());
    }
    
    const noticedMatch = reportText.match(/What We Noticed[🔎:\s]*([^💬🌻]+)/i);
    if (noticedMatch) {
      parsed.whatWeNoticed = noticedMatch[1]
        .split(/[\n•\-]/)
        .filter(s => s.trim())
        .map(s => s.trim());
    }
    
    const homeMatch = reportText.match(/Home Connection[💬:\s]*([^🌻]+)/i);
    if (homeMatch) parsed.homeConnection = homeMatch[1].trim();
    
    const seedMatch = reportText.match(/Tomorrow'?s Seed[🌻:\s]*(.+)/i);
    if (seedMatch) parsed.tomorrowSeed = seedMatch[1].trim();
    
    return parsed;
  };
  
  const parsedReport = parseReport(report);
  
  // Use parsed values or fall back to props
  const displaySnapshot = snapshot || parsedReport.snapshot || (transcript ? transcript.substring(0, 200) + '...' : 'Session summary will appear here.');
  const displayBrightSpots = brightSpots.length > 0 ? brightSpots : (parsedReport.brightSpots || ['Engaged in conversation', 'Showed enthusiasm']);
  const displayNoticed = whatWeNoticed.length > 0 ? whatWeNoticed : (parsedReport.whatWeNoticed || ['Observations will be added after review']);
  const displayHomeConnection = homeConnection || parsedReport.homeConnection || 'Ask about what they shared today. Even 5 minutes of conversation reinforces their growth.';
  const displayTomorrowSeed = tomorrowSeed || parsedReport.tomorrowSeed || 'Continue building trust through daily conversations.';
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Today';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };
  
  // Get first name
  const firstName = childName?.split(' ')[0] || 'Child';
  
  if (compact) {
    return (
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-3 border border-emerald-200">
        <div className="flex items-center justify-between mb-2">
          <h5 className="font-medium text-emerald-800 text-sm">
            🌱 {firstName}'s Daily Connect
          </h5>
          <span className="text-xs text-emerald-600">📅 {formatDate(date)}</span>
        </div>
        <p className="text-sm text-gray-600">{displaySnapshot.substring(0, 100)}...</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">✨ {displayBrightSpots.length} bright spots</span>
          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">🔎 {displayNoticed.length} observations</span>
        </div>
      </div>
    );
  }
  
  return (
    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 overflow-hidden">
      <CardContent className="p-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 text-white">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold flex items-center gap-2">
              <span className="text-xl">🌱</span> {firstName}'s Daily Connect
            </h4>
            <span className="text-emerald-100 text-sm">📅 {formatDate(date)}</span>
          </div>
          {focus && (
            <p className="text-emerald-100 text-sm mt-1">
              🎯 Today's Focus: {focus}
            </p>
          )}
        </div>
        
        <div className="p-4 space-y-4">
          {/* Snapshot */}
          <div>
            <h5 className="font-semibold text-gray-800 mb-2 text-sm">Snapshot</h5>
            <p className="text-gray-600 bg-white/60 rounded-lg p-3 text-sm leading-relaxed">
              {displaySnapshot}
            </p>
          </div>
          
          {/* Bright Spots */}
          <div>
            <h5 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-1">
              Bright Spots <span>✨</span>
            </h5>
            <ul className="space-y-2 bg-white/60 rounded-lg p-3">
              {displayBrightSpots.map((spot, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="text-emerald-500 mt-0.5 flex-shrink-0">•</span>
                  <span>{spot}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* What We Noticed */}
          <div>
            <h5 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-1">
              What We Noticed <span>🔎</span>
            </h5>
            <div className="bg-amber-50 rounded-lg p-3 border-l-4 border-amber-400">
              {displayNoticed.map((notice, idx) => (
                <p key={idx} className="text-sm text-gray-600 mb-1 last:mb-0">
                  {notice}
                </p>
              ))}
            </div>
          </div>
          
          {/* Home Connection */}
          <div>
            <h5 className="font-semibold text-gray-800 mb-2 text-sm flex items-center gap-1">
              Home Connection <span>💬</span>
            </h5>
            <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-400">
              <p className="text-sm text-gray-600">{displayHomeConnection}</p>
            </div>
          </div>
          
          {/* Tomorrow's Seed */}
          <div className="bg-gradient-to-r from-amber-100 to-yellow-100 rounded-lg p-3">
            <h5 className="font-semibold text-amber-800 mb-1 text-sm flex items-center gap-1">
              Tomorrow's Seed <span>🌻</span>
            </h5>
            <p className="text-sm text-amber-700">{displayTomorrowSeed}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyInsight;
